import { IonChip, IonLabel } from '@ionic/react';
import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HELPER_BEE_OPENED_STORAGE_KEY, HelperBeeViewModel, IHelperBeeTrackingFrom } from './HelperBeeViewModel';
import { useSubscription } from '@meraki-internal/state';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { useAsyncEffect } from '../support/useAsyncEffect';

const useStyles = createUseStyles({
    chip: {
        height: 40,
        color: '#fff',
        paddingLeft: 6,
        borderRadius: 24,
        overflow: 'visible',
        textAlign: 'center',
        border: '2px solid #fff',
        '--background': 'linear-gradient(rgba(22, 166, 217, 1), rgba(8, 63, 82, 1))',

        '& > img': {
            width: '13%'
        },

        '& ion-label': {
            flexGrow: 1,
            fontWeight: 500
        }
    },

    badge: {
        width: 24,
        height: 24,
        top: -8,
        right: -8,
        borderRadius: '50%',
        position: 'absolute',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ion-color-danger)',
        boxShadow: '1px 1px 10px 0 rgba(0, 0, 0, 0.4)'
    }
});

export type IHelperBeeButtonProps = {
    label?: string;
    'data-id'?: string;
    coloredBorder?: boolean;
    trackingFrom: IHelperBeeTrackingFrom;
};

const labels = [
    'Need caregiving help?',
    'Got a care question?',
    'Your care consultant here!',
    'Bea is here to help!',
    'Looking for answers?',
    'Chat with Bea for help!',
    'Ask Bea anything!',
    'Care questions? Just ask!',
    'Need support? Talk to Bea!',
    'Your Care Consultant is here!',
    'Get support here!',
    'Caregiving help is here!',
    'Need a Care Consultant?',
    'Help is one tap away!',
    'Ask Bea, get answers!',
    `Bea's got your back!`
];

export const HelperBeeButton: FC<IHelperBeeButtonProps> = ({
    label,
    trackingFrom,
    'data-id': dataId,
    coloredBorder
}) => {
    const classes = useStyles();

    const storageProvider = useInstance(AsyncStorageProvider);
    const chatModalVM = useInstance(HelperBeeViewModel);
    useSubscription(() => chatModalVM);

    const [showBadge, setShowBadge] = useState<boolean | undefined>(false);
    const [realLabel, setRealLabel] = useState<string | undefined>(label);

    useEffect(() => {
        if (!label) {
            setRealLabel(labels[Math.floor(Math.random() * labels.length)]);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useAsyncEffect(async () => {
        const value = await storageProvider.getBooleanProvider(HELPER_BEE_OPENED_STORAGE_KEY).get();
        setShowBadge(!value);
    }, [chatModalVM.state]); // eslint-disable-line react-hooks/exhaustive-deps

    const onClickChip = () => {
        chatModalVM.open({ trackingFrom, label: realLabel });
    };

    return (
        <div data-id={dataId} style={{maxWidth: 260}}>
            <IonChip
                onClick={onClickChip}
                className={classes.chip}
                style={coloredBorder ? {border: '2px solid #b6e3f4'} : {}}
            >
                <img alt="HelperBee" src="/assets/images/help/helper-bea.png" />
                <IonLabel style={{marginTop: -2}}>{realLabel}</IonLabel>
                {showBadge && <div data-id="helper-bee-badge" className={classes.badge}>1</div>}
            </IonChip>
        </div>
    );
};
